import { computed } from 'vue'
import { freezeObject } from '@/global/utils/utils'
import TabActionButtons from '@/global/components/view-layouts/tabs-layout/components/TabActionButtons.vue'
import TaskDetailsInfo from '@/modules/road-maintenance-module/winter-service-v2/tasks/components/TaskDetailsInfo.vue'
import { useTaskFields } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskFields'
import { useTaskVehicleFacts } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskVehicleFacts'
import { toTaskPayload } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/taskData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  TASKS_ENDPOINT,
  TASKS_INDEX_ROUTE,
  TASKS_TRANSLATION_NAMESPACE as NS,
  TASK_BASIC_INFO_TAB_KEY,
  TASK_DETAILS_ROUTE
} from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'

/**
 * The new task screen - the details screen without a record behind it.
 *
 * Only the basic information is offered: the create endpoint takes the task's own
 * columns and nothing else, and a street can only be drawn under a task that already
 * exists. That is why the screen shows no tabs at all rather than a single one - the
 * route tab opens once the task has been saved and the details screen takes over.
 */
export function useTaskCreate (options) {
  const { translate, router, returnToTaskId } = options || {}

  // The same fields as the details screen, read-only ones included: picking a
  // registration is what fills them in, so the dispatcher sees what the vehicle brings
  // with it before saving. They are `creatable: false`, so none of them is sent - the
  // task points at the vehicle and takes the rest from it.
  const { rows } = useTaskFields({ translate })
  const { clear: clearVehicleFacts } = useTaskVehicleFacts()

  // A task left half-created still leaves the last picked vehicle behind - the facts
  // are shared by every task screen, so a fresh form starts with none.
  clearVehicleFacts()

  const apiConfig = freezeObject({
    post: {
      module: WINTER_SERVICE_MODULE,
      route: TASKS_ENDPOINT
    }
  })

  /**
   * Back out of creating. The screen is reached both from the listing and from a task's
   * own details header, so it goes back to whichever of the two opened it - the details
   * screen says so with a `returnTo` query.
   */
  const goBack = () => {
    if (returnToTaskId) {
      router?.push({
        name: TASK_DETAILS_ROUTE,
        params: {
          id: String(returnToTaskId)
        }
      })
      return
    }

    router?.push({ name: TASKS_INDEX_ROUTE })
  }

  const tabActions = computed(() => ({
    component: TabActionButtons,
    title: translate(`${NS}.new_task`),
    showEditButtons: true,
    showBackButton: {
      onGoBack: goBack
    },
    onCancelTabSave: goBack,
    visible: true
  }))

  // See useTaskDetails - `rows` is what Content.vue reads to build the save payload,
  // so the fields have to live there.
  const infoBlock = computed(() => ({
    component: TaskDetailsInfo,
    visible: true,
    rows: rows.value
  }))

  const afterSave = (context, data) => {
    const { id } = data || {}

    if (!id) {
      return
    }

    router?.replace({
      name: TASK_DETAILS_ROUTE,
      params: {
        id: String(id)
      }
    })
  }

  return computed(() => ({
    tabs: [
      {
        key: TASK_BASIC_INFO_TAB_KEY,
        title: translate(`${NS}.new_task`),
        apiConfig,
        afterSave,
        // The status dropdown is hidden while creating, but a payload that does carry
        // one has to speak the backend's `is_active`.
        modifyDataBeforeSend: ({ copiedData }) => toTaskPayload(copiedData),
        tabContent: [
          tabActions.value,
          infoBlock.value
        ]
      }
    ]
  }))
}
